import type { VariantParametric } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";

const normalize = (value: string | null | undefined): string =>
	value?.trim().toLowerCase() ?? "";

/**
 * Parametrics for the PDP specification tab: those with a value that Norce
 * doesn't flag hidden, minus codes a template leaves out (e.g. colour, which
 * breadandboxers already shows as the swatch picker).
 *
 * `allowedCodes`, when given, is the whole list a shopper may see. A space that
 * keeps working data in parametrics (sportkost: deposit flags, the variant
 * family's part numbers, pack configuration) cannot rely on Norce's hidden
 * flag being set on each new one, so it names the few meant for customers
 * instead. Omitted means every code, as before.
 *
 * `hiddenEntries` are dropped by name or code, case-insensitively — what an
 * editor types in Storyblok, who sees the name on the page, not the code.
 */
export const getVisibleSpecifications = (
	parametrics: VariantParametric[] | undefined,
	excludedCodes: readonly string[] = [],
	allowedCodes?: readonly string[],
	hiddenEntries: readonly string[] = []
): VariantParametric[] => {
	const hidden = new Set(hiddenEntries.map(normalize));
	return (parametrics ?? []).filter(
		({ Code, Name, Value, IsHidden }) =>
			!!Value &&
			!IsHidden &&
			!excludedCodes.includes(Code) &&
			(!allowedCodes || allowedCodes.includes(Code)) &&
			!hidden.has(normalize(Code)) &&
			!hidden.has(normalize(Name))
	);
};

/** A Storyblok text field's entries: one per line or comma, trimmed. */
export const parseSpecificationList = (
	text: string | null | undefined
): string[] =>
	(text ?? "")
		.split(/[\n,]/)
		.map((entry) => entry.trim())
		.filter(Boolean);

/**
 * The parametrics to hide: the product page layout's `hidden_specifications`
 * when the editor filled it in, otherwise the template's app-config default.
 */
export const resolveHiddenSpecifications = (
	blokText: string | null | undefined,
	templateDefault: readonly string[]
): string[] => {
	const fromBlok = parseSpecificationList(blokText);
	return fromBlok.length ? fromBlok : [...templateDefault];
};
